import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import SellerRegisterModal from "./SellerRegisterModal";

const SellerLogin = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");

  const otpInputs = useRef([]);
  const navigate = useNavigate();
  const { supplierLogin } = useAuth();
  const CORRECT_OTP = "111111";

  useEffect(() => {
    AOS.init({ duration: 700, once: true });
  }, []);

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!phoneNumber) {
      setError("Enter your email or mobile phone number");
      return;
    }

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/supplier/suppliers/login`,
        { phoneNumber }
      );

      if (data.success) {
        toast.success(t("sellerLogin.otp_sent") || "OTP Sent (Demo: 111111)");
        setStep("otp");
        setTimeout(() => otpInputs.current[0]?.focus(), 300);
      } else {
        const backendError = data.error;
        if (backendError && !backendError.includes("Account not found")) {
            setError(backendError);
        } else {
            const isEmail = phoneNumber.includes("@");
            setError(isEmail ? "We cannot find an account with that email address" : "We cannot find an account with that mobile number");
        }
      }
    } catch (err) {
      const backendError = err.response?.data?.error;
      const status = err.response?.status;
      
      if (status === 404 || !backendError || backendError.includes("Account not found")) {
        const isEmail = phoneNumber.includes("@");
        setError(isEmail ? "We cannot find an account with that email address" : "We cannot find an account with that mobile number");
      } else {
        setError(backendError);
      }
    }
  };

  const handleOtpChange = (i, value) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otp];
    updated[i] = value;
    setOtp(updated);
    if (value && i < 5) otpInputs.current[i + 1]?.focus();
  };

  const handleOtpKeyDown = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) {
      otpInputs.current[i - 1]?.focus();
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const enteredOtp = otp.join("");

    if (enteredOtp !== CORRECT_OTP) {
      setError(t("sellerLogin.invalid_otp") || "Invalid OTP");
      toast.error(t("sellerLogin.invalid_otp") || "Invalid OTP");
      setOtp(["", "", "", "", "", ""]);
      otpInputs.current[0]?.focus();
      return;
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/supplier/suppliers/login`,
        { phoneNumber }
      );

      if (res.data.success) {
        toast.success(t("sellerLogin.login_success") || "Login Successful!");
        localStorage.setItem("login-data", JSON.stringify(res.data.data));
        supplierLogin(res.data.data);
        setTimeout(() => navigate("/seller/dashboard"), 1200);
      }
    } catch (err) {
      toast.error(t("sellerLogin.login_failed") || "Login failed");
    }
  };

  return (
    <>
      <Toaster position="top-center" />
      {step === "register" ? (
        <SellerRegisterModal closeModal={() => setStep("phone")} />
      ) : (
      <div className="min-h-screen bg-white flex flex-col items-center pt-8 p-4 font-sans text-[#111]">
        {/* Amazon-like Logo */}
        <div className="mb-6 cursor-pointer" onClick={() => navigate("/")}>
          <img 
            src={require("../../Assets/Logos/Bharat Karigari N.png")} 
            alt="Bharat Karigari Logo" 
            className="h-12 object-contain"
          />
        </div>

        {error && (
          <div className="w-full max-w-[350px] mb-4 border border-[#c40000] rounded-[3px] p-4 flex gap-3 shadow-[0_0_0_4px_rgba(221,0,0,0.1)] bg-white" data-aos="fade-in">
            <div className="text-[#c40000] pt-0.5">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-alert-circle"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </div>
            <div>
              <h4 className="text-[#c40000] font-bold text-[13px] mb-0.5">There was a problem</h4>
              <p className="text-[13px] m-0 text-[#111]">{error}</p>
            </div>
          </div>
        )}

        <div
          className="bg-white w-full max-w-[350px] border border-[#ddd] rounded-[4px] p-6 shadow-[0_1px_2px_rgba(0,0,0,0.2)]"
          data-aos="zoom-in"
        >
          {/* PHONE STEP */}
          {step === "phone" && (
            <>
              <h1 className="text-[28px] font-normal mb-4">Sign in</h1>
              <form onSubmit={handlePhoneSubmit}>
                <div className="mb-4">
                  <label className="block text-[13px] font-bold mb-1">
                    Email or mobile phone number
                  </label>
                  <input
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => {
                      setError("");
                      setPhoneNumber(e.target.value);
                    }}
                    className={`w-full px-3 py-2 border ${error && step === "phone" ? 'border-[#c40000] shadow-[0_0_0_3px_rgba(221,0,0,0.1)] focus:border-[#c40000]' : 'border-[#a6a6a6] focus:border-[#e77600] focus:shadow-[0_0_3px_2px_rgba(228,121,17,0.5)]'} rounded-[3px] focus:outline-none text-[14px]`}
                  />
                </div>

                <button className="w-full py-1.5 bg-[#f0c14b] border border-[#a88734_#9c7e31_#846a29] hover:bg-[#f4d078] rounded-[3px] text-[13px] text-[#111] shadow-[0_1px_0_rgba(255,255,255,0.4)_inset]">
                  Continue
                </button>
              </form>

              <div className="mt-4 text-[12px] leading-relaxed">
                By continuing, you agree to Bharat Karigari's <span className="text-[#0066c0] hover:text-[#c45500] hover:underline cursor-pointer">Conditions of Use</span> and <span className="text-[#0066c0] hover:text-[#c45500] hover:underline cursor-pointer">Privacy Notice</span>.
              </div>

              <div className="mt-6 pt-4 border-t border-[#e7e7e7]">
                <div className="text-[13px] font-bold mb-2">Are you new to Seller Hub?</div>
                <div className="text-[12px] text-[#0066c0] hover:text-[#c45500] hover:underline cursor-pointer">
                  Need help?
                </div>
              </div>
            </>
          )}

          {/* OTP STEP */}
          {step === "otp" && (
            <>
              <h1 className="text-[28px] font-normal mb-2">Verification required</h1>
              <p className="text-[13px] mb-4">
                To continue, complete this verification step. We've sent an OTP to the email/mobile number <strong className="font-bold">{phoneNumber}</strong>. 
                <span onClick={() => setStep("phone")} className="text-[#0066c0] hover:text-[#c45500] hover:underline cursor-pointer ml-1">Change</span>
              </p>

              <form onSubmit={handleOtpSubmit}>
                <div className="mb-4">
                  <label className="block text-[13px] font-bold mb-2">Enter OTP</label>
                  <div className="flex justify-between">
                    {otp.map((digit, i) => (
                      <input
                        key={i}
                        ref={(el) => (otpInputs.current[i] = el)}
                        value={digit}
                        maxLength="1"
                        onChange={(e) => handleOtpChange(i, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                        className="w-10 h-10 border border-[#a6a6a6] rounded-[3px] text-center text-[16px] focus:outline-none focus:border-[#e77600] focus:shadow-[0_0_3px_2px_rgba(228,121,17,0.5)]"
                      />
                    ))}
                  </div>
                  {error && <p className="text-[#c40000] text-xs mt-2">{error}</p>}
                </div>

                <button className="w-full py-1.5 bg-[#f0c14b] border border-[#a88734_#9c7e31_#846a29] rounded-[3px] text-[13px] text-[#111] shadow-[0_1px_0_rgba(255,255,255,0.4)_inset]">
                  Verify & Sign in
                </button>
              </form>

              <p className="text-[12px] text-center mt-4 text-[#767676]">
                Demo OTP: <strong>111111</strong>
              </p>
            </>
          )}
        </div>

        {/* Create account button (Amazon style divider) */}
        {step === "phone" && (
          <div className="w-full max-w-[350px] mt-6">
            <div className="relative text-center mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#e7e7e7]"></div>
              </div>
              <div className="relative">
                <span className="bg-white px-2 text-[12px] text-[#767676]">New to Bharat Karigari?</span>
              </div>
            </div>
            
            <button 
              className="w-full py-1.5 bg-[#e7e9ec] border border-[#adb1b8_#a2a6ac_#8d9096] hover:bg-[#d9dce1] rounded-[3px] text-[13px] text-[#111] shadow-[0_1px_0_rgba(255,255,255,0.6)_inset]"
              onClick={() => {
                setStep("register");
              }}
            >
              Create your Seller account
            </button>
          </div>
        )}
        
        <div className="w-full max-w-[350px] mt-10 text-center text-[11px] text-[#555]">
          <div className="flex justify-center gap-6 mb-2">
            <span className="text-[#0066c0] hover:text-[#c45500] hover:underline cursor-pointer">Conditions of Use</span>
            <span className="text-[#0066c0] hover:text-[#c45500] hover:underline cursor-pointer">Privacy Notice</span>
            <span className="text-[#0066c0] hover:text-[#c45500] hover:underline cursor-pointer">Help</span>
          </div>
          <div>© 2024-2026, BharatKarigari.com, Inc. or its affiliates</div>
        </div>
      </div>
      )}
    </>
  );
};

export default SellerLogin;
